import { reactive } from 'vue';
import axios from 'axios';
import { buildVizProps } from '@/utils/metricInsightAdapter';

const SESSIONS = new Map();

const LABELS = {
  cell: 'Query', draft: 'Draft', visual: 'Visual', drawing: 'Drawing', page: 'Page', dashboard: 'Dashboard',
  plan: 'Plan', file: 'File', document: 'Document', proposal: 'Schema proposal', report: 'Report', workbook: 'Excel workbook', infotag: 'Entity tag',
  template_run: 'Template run', frame: 'Saved frame', saved_page: 'Page',
  metric: 'Metric', data_attribute: 'DataAttribute', flag: 'Patient flag', worklist: 'Worklist',
  insight: 'Insight', dataset: 'Dataset', schema: 'Parser schema', routine: 'Routine',
  task_type: 'Task type', task_action: 'Task action', metric_workflow: 'Metric workflow',
  task_status: 'Task status', meeting_type: 'Meeting type', template: 'Template', widget: 'Widget',
  data_workflow: 'Workflow', work_type: 'Work type', work_item: 'Work item',
};
const DRAFT_LABELS = { data_attribute: 'DataAttribute', metric: 'Metric', dataset: 'Dataset', schema: 'Parser schema' };
const ENGINE_LABELS = { snowflake: 'Snowflake', postgres: 'Postgres' };
const VERSIONED = ['code', 'spec', 'html', 'svg', 'sources', 'widgets'];
const WORKBENCH_KINDS = new Set(['cell', 'draft', 'visual', 'drawing', 'page', 'dashboard', 'plan', 'file', 'document', 'proposal', 'template_run']);
const LEGACY_WORKBENCH_TYPES = new Set(['block', 'block_result', 'block_run', 'r_output', 'r_code', 'draft', 'draft_removed', 'visual', 'drawing',
  'sources_update', 'file_sample', 'file_text', 'file_ready', 'schema_proposal', 'page_preview', 'page_saved', 'artifacts_removed']);
const CONVERSATION_EVENT_TYPES = new Set(['user', 'text', 'text_delta', 'text_end', 'thinking_start', 'thinking_delta', 'thinking_end']);

function labelFor(item) {
  if (item.kind === 'cell') return ENGINE_LABELS[item.data.engine] || 'Query';
  if (item.kind === 'draft') return DRAFT_LABELS[item.data.kind] || 'Draft';
  return LABELS[item.kind] || 'Artifact';
}

function sessionEntry(key) {
  if (!SESSIONS.has(key)) {
    SESSIONS.set(key, {
      key,
      state: reactive({
        connState: 'idle',
        closeReason: '',
        blocks: [],
        items: [],
        totalCost: 0,
        thinkingSince: null,
        retryInSeconds: null,
        title: '',
        description: '',
        files: [],
        promptTokens: null,
        contextLimit: null,
        continuedBy: '',
        notebookId: '',
        viewer: null,
        shared: false,
        tools: null,
        hat: null,
        path: [],
        workerAlive: false,
      }),
      order: 0,
      sessionId: null,
      cursor: 0,
      ownSeqs: new Set(),
      pollToken: 0,
      pollActive: false,
      posting: 0,
      lastEventAt: 0,
      itemsTimer: null,
      view: null,
      onWorkerOff: null,
      replayAt: null,
      pendingTurn: false,
      rowQueue: [],
      rowLoading: false,
    });
  }
  return SESSIONS.get(key);
}

function findEntryBySession(sessionId) {
  if (!sessionId) return null;
  for (const entry of SESSIONS.values()) {
    if (entry.sessionId === sessionId) return entry;
  }
  return null;
}

function entryForSession(sessionId, fallbackKey) {
  return findEntryBySession(sessionId) || sessionEntry(sessionId ? `session:${sessionId}` : fallbackKey);
}

const agentOverlay = reactive({ open: false });

function turnUrl(entry) {
  return `/api/impact_analytics/agent-sessions/${entry.sessionId}/turn/`;
}

function applyEnvelope(entry, data) {
  const s = entry.state;
  const map = {
    idle: 'ready', thinking: 'thinking', launching: 'launching',
    rate_limited: 'rate_limited', closed: 'closed',
  };
  let st = map[data.state] || 'ready';
  if (st === 'closed' && data.status !== 'died') st = 'ready';
  if (entry.pendingTurn && !data.worker_alive && st !== 'launching') entry.pendingTurn = false;
  if (entry.pendingTurn && st === 'ready') st = 'thinking';
  if (st === 'thinking' && s.connState !== 'thinking') s.thinkingSince = Date.now();
  if (st !== 'thinking') s.thinkingSince = null;
  s.connState = st;
  s.retryInSeconds = data.retry_in_seconds ?? null;
  s.workerAlive = !!data.worker_alive;
  if (typeof data.total_cost_usd === 'number') s.totalCost = data.total_cost_usd;
  if (data.title) s.title = data.title;
  if (typeof data.description === 'string') s.description = data.description;
  if (typeof data.prompt_tokens === 'number') s.promptTokens = data.prompt_tokens;
  if (typeof data.context_limit === 'number') s.contextLimit = data.context_limit;
  if (data.continued_by) s.continuedBy = data.continued_by;
  if (typeof data.notebook_id === 'string') s.notebookId = data.notebook_id;
  s.viewer = data.viewer || null;
  if (typeof data.shared === 'boolean') s.shared = data.shared;
  if (st === 'closed') s.closeReason = 'The agent process died.';
}

function hasPendingQuestion(entry) {
  return entry.state.blocks.some((b) => ['question', 'approval'].includes(b.kind) && !b.answered);
}

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

async function pollOnce(entry) {
  const { data } = await axios.get(
    `/api/impact_analytics/agent-sessions/${entry.sessionId}/stream/`,
    { params: { after: entry.cursor } },
  );
  let fresh = 0;
  for (const ev of data.events || []) {
    if (ev.seq > entry.cursor) entry.cursor = ev.seq;
    if (entry.ownSeqs.has(ev.seq)) continue;
    handleEvent(entry, ev);
    fresh += 1;
  }
  applyEnvelope(entry, data);
  return fresh;
}

function wakePolling(entry) {
  if (entry.pollActive || !entry.sessionId) return;
  const token = ++entry.pollToken;
  entry.pollActive = true;
  entry.lastEventAt = Date.now();
  (async () => {
    let errorDelay = 1000;
    let readyPolls = 0;
    try {
      while (entry.pollToken === token) {
        if (document.hidden) { await sleep(1000); continue; }
        if (entry.posting > 0) { await sleep(100); continue; }
        let fresh = 0;
        try {
          fresh = await pollOnce(entry);
          errorDelay = 1000;
        } catch (err) {
          const code = err && err.response && err.response.status;
          if (code === 404 || code === 401 || code === 403) {
            entry.state.connState = 'closed';
            entry.state.closeReason = 'Session unavailable.';
            return;
          }
          await sleep(errorDelay);
          errorDelay = Math.min(errorDelay * 2, 30000);
          continue;
        }
        if (fresh) entry.lastEventAt = Date.now();
        const st = entry.state.connState;
        if (st === 'closed') return;
        if (st === 'launching') {
          await sleep(1500);
          continue;
        }
        if (st === 'ready' || hasPendingQuestion(entry)) {
          if (!entry.state.workerAlive && entry.onWorkerOff) entry.onWorkerOff();
          readyPolls += 1;
          await sleep(readyPolls >= 5 && !fresh ? 20000 : 1000);
          continue;
        }
        readyPolls = 0;
        if (st === 'rate_limited') { await sleep(1500); continue; }
        const quiet = Date.now() - entry.lastEventAt;
        if (quiet < 6000) await sleep(300);
        else if (quiet < 30000) await sleep(1000);
        else await sleep(2500);
      }
    } finally {
      if (entry.pollToken === token) entry.pollActive = false;
    }
  })();
}

function stopPolling(entry) {
  entry.pollToken += 1;
  entry.pollActive = false;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      SESSIONS.forEach((entry) => {
        const st = entry.state.connState;
        if (entry.sessionId && st !== 'closed' && st !== 'idle') wakePolling(entry);
      });
    }
  });
}

function itemKey(kind, id) {
  return `${kind}:${id}`;
}

function findItem(entry, key) {
  return entry.state.items.find((it) => it.key === key) || null;
}

function newItem(entry, key) {
  const i = key.indexOf(':');
  const item = {
    key, kind: key.slice(0, i), id: key.slice(i + 1), label: '', order: ++entry.order, at: null, seq: null,
    data: {}, runs: [], versions: [], saved: {}, live: null,
    editing: false, edit: {}, editError: '', saving: false, error: '', collapsed: true, current: null, runIndex: null, flash: false, cancelling: false, expanded: false,
  };
  entry.state.items.push(item);
  return item;
}

function vizFor(data) {
  try {
    const viz = buildVizProps(data.viz_type, data.viz_config, data.rows || []);
    if (viz && viz.props && data.inputs && typeof data.inputs === 'object') viz.props.inputs = data.inputs;
    return viz;
  } catch {
    return null;
  }
}

function snapshot(item) {
  const out = { n: item.data.version || 1, at: item.at };
  VERSIONED.forEach((k) => { if (k in item.data) out[k] = item.data[k]; });
  return out;
}

function reduceItem(entry, ev) {
  const key = String(ev.key || '');
  if (!key.includes(':')) return null;
  if (ev.op === 'remove') {
    entry.state.items = entry.state.items.filter((it) => it.key !== key);
    return null;
  }
  let item = findItem(entry, key);
  const created = !item;
  if (!item) {
    if (ev.if_exists) return null;
    item = newItem(entry, key);
  }
  if (ev.at) item.at = ev.at;
  if (ev.op === 'put') {
    const data = { ...(ev.data || {}) };
    const bump = data.bump;
    const versionSaved = data.version_saved;
    delete data.bump;
    delete data.version_saved;
    const current = item.data.version || (Object.keys(item.data).length ? 1 : 0);
    const changed = (bump || []).some((k) => k in data && JSON.stringify(data[k]) !== JSON.stringify(item.data[k]));
    if (bump && changed && !('version' in data)) data.version = current + 1;
    if (data.version && current && data.version > current) {
      item.versions.push(snapshot(item));
      item.current = null;
      if (item.editing) { item.editing = false; item.edit = {}; }
    }
    Object.assign(item.data, data);
    if (ev.seq && (item.kind === 'visual' || item.kind === 'drawing' || 'rows' in data)) item.seq = ev.seq;
    if (versionSaved) {
      const n = versionSaved.n || item.data.version || 1;
      const rest = { ...versionSaved };
      delete rest.n;
      item.saved = { ...item.saved, [String(n)]: rest };
    }
    if (item.kind === 'visual') {
      if (item.data.rows_deferred && !item.data.rows) {
        item.viz = null;
        entry.rowQueue = entry.rowQueue.filter((q) => q.key !== key);
        entry.rowQueue.push({ key, seq: item.seq || ev.seq });
        loadVisualRows(entry);
      } else {
        item.viz = vizFor(item.data);
      }
    }
  } else if (ev.op === 'run') {
    const run = { ...(ev.run || {}) };
    run.id = String(run.id || '');
    if (!run.id) return item;
    const existing = item.runs.find((r) => r.id === run.id);
    if (existing) Object.assign(existing, run);
    else item.runs.push(run);
    item.runIndex = null;
  }
  item.label = labelFor(item);
  if (created && WORKBENCH_KINDS.has(item.kind)) {
    item.expanded = true;
    if (entry.view) entry.view.scrollArtifacts();
  }
  return item;
}

function mergeServerItems(entry, list) {
  const keep = new Set();
  for (const server of list || []) {
    keep.add(server.key);
    let item = findItem(entry, server.key);
    if (!item) item = newItem(entry, server.key);
    item.order = typeof server.order === 'number' ? server.order : item.order;
    item.at = server.at || item.at;
    item.seq = server.seq || item.seq;
    item.data = { ...item.data, ...(server.data || {}) };
    if (server.data && server.data.rows_deferred && !item.data.rows) delete item.data.rows;
    item.runs = server.runs || [];
    item.versions = server.versions || [];
    item.saved = server.saved || {};
    item.live = server.live || null;
    item.label = server.label || labelFor(item);
    if (item.kind === 'visual') {
      if (item.data.rows_deferred && !item.data.rows) {
        item.viz = null;
        if (item.seq) {
          entry.rowQueue = entry.rowQueue.filter((q) => q.key !== item.key);
          entry.rowQueue.push({ key: item.key, seq: item.seq });
        }
      } else item.viz = vizFor(item.data);
    }
  }
  entry.state.items = entry.state.items.filter((it) => keep.has(it.key)).sort((a, b) => a.order - b.order);
  entry.order = Math.max(entry.order, ...entry.state.items.map((it) => it.order), 0);
  if (!entry.state.items.some((it) => it.expanded) && entry.state.items.length) entry.state.items[entry.state.items.length - 1].expanded = true;
  if (entry.rowQueue.length) loadVisualRows(entry);
  if (entry.view) {
    entry.state.items.forEach((it) => {
      if (it.kind === 'template_run' && ['pending', 'running'].includes((it.live || it.data).status)) entry.view.startTemplateRunPolling(it.id);
    });
  }
}

function scheduleItemsRefresh(entry) {
  clearTimeout(entry.itemsTimer);
  entry.itemsTimer = setTimeout(() => fetchItems(entry), 400);
}

async function fetchItems(entry) {
  if (!entry.sessionId) return;
  try {
    const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${entry.sessionId}/items/`);
    mergeServerItems(entry, data.items || []);
  } catch {
    // the workbench refreshes again on the next turn
  }
}

async function loadVisualRows(entry) {
  if (entry.rowLoading || !entry.sessionId) return;
  entry.rowLoading = true;
  try {
    while (entry.rowQueue.length) {
      const next = entry.rowQueue.pop();
      const item = findItem(entry, next.key);
      if (!item || !item.data.rows_deferred || item.data.rows) continue;
      try {
        const { data } = await axios.get(`/api/impact_analytics/agent-sessions/${entry.sessionId}/events/${next.seq}/`);
        const payload = data.type === 'item' ? (data.data || {}) : data;
        item.data = { ...item.data, rows: payload.rows || [], inputs: payload.inputs, rows_deferred: false };
        item.viz = vizFor(item.data);
      } catch {
        item.viz = null;
      }
    }
  } finally {
    entry.rowLoading = false;
  }
}

function itemForRun(entry, runId) {
  const id = String(runId);
  return entry.state.items.find((it) => it.runs.some((r) => r.id === id)) || null;
}

function versionsOf(item) {
  const current = snapshot(item);
  const list = [...item.versions, current];
  return list.map((v) => ({ ...v, saved: item.saved[String(v.n)] || null }));
}

function currentVersion(item) {
  const list = versionsOf(item);
  const index = typeof item.current === 'number' && item.current >= 0 && item.current < list.length ? item.current : list.length - 1;
  return { index, version: list[index], versions: list };
}

function showVersion(entry, key, index) {
  const item = findItem(entry, key);
  if (!item) return;
  item.current = index;
}

function attachFile(entry, name, key) {
  if (!key) return;
  const files = entry.state.files.filter((f) => f.key !== key);
  files.push({ name: name || String(key).split('/').pop(), key });
  entry.state.files = files;
}

function removeItem(entry, item) {
  entry.state.items = entry.state.items.filter((it) => it.key !== item.key);
  if (entry.sessionId) {
    axios.post(turnUrl(entry), { type: 'remove_artifacts', keys: [item.key] })
      .then(({ data }) => { if (data.seq) entry.ownSeqs.add(data.seq); })
      .catch(() => {});
  }
}

async function deleteSavedPage(entry, item) {
  const pageIds = new Set(Object.values(item.saved || {}).map((v) => v.page_id).filter(Boolean));
  if (item.kind === 'saved_page') pageIds.add(item.id);
  if (item.data.page_id) pageIds.add(item.data.page_id);
  for (const pageId of pageIds) {
    try {
      await axios.delete(`/api/impact_analytics/agent-pages/${pageId}/`);
    } catch (err) {
      pushBlock(entry, { kind: 'error', text: err?.response?.data?.error || `Page ${pageId} could not be deleted.` });
      return false;
    }
  }
  removeItem(entry, item);
  return true;
}

function applyDraftEdit(entry, id, spec) {
  const item = findItem(entry, itemKey('draft', id));
  if (!item || !spec || typeof spec !== 'object') return null;
  if (JSON.stringify(spec) === JSON.stringify(item.data.spec)) return item;
  return reduceItem(entry, { op: 'put', key: item.key, data: { spec: { ...spec }, by: 'user', bump: ['spec'], ...(item.data.saved_id ? { status: 'changed' } : {}) } });
}

function applyCellEdit(entry, id, engine, code) {
  const item = findItem(entry, itemKey('cell', id));
  if (!item) return null;
  return reduceItem(entry, { op: 'put', key: item.key, data: { code, engine, by: 'user', bump: ['code'] } });
}

function pushBlock(entry, block) {
  if (entry.replayAt && block.at === undefined) block.at = entry.replayAt;
  entry.state.blocks.push(block);
  if (entry.view) entry.view.scrollChat();
}

function thinkingBlock(entry, id) {
  const last = entry.state.blocks[entry.state.blocks.length - 1];
  if (last && last.kind === 'thinking') {
    last.id = id;
    last.done = false;
    return last;
  }
  const block = { kind: 'thinking', id, text: '', done: false, collapsed: true };
  pushBlock(entry, block);
  return block;
}

function handleEvent(entry, ev) {
  const s = entry.state;
  if (ev.type === 'ping') return;
  if (ev.type === 'status') {
    if (ev.state === 'launching' || ev.state === 'starting') s.connState = 'launching';
    else if (ev.state === 'rate_limited') {
      s.connState = 'rate_limited';
      s.retryInSeconds = ev.retry_in_seconds;
    } else if (ev.state === 'ready') {
      s.connState = 'ready';
      s.thinkingSince = null;
    } else if (ev.state === 'thinking') {
      s.connState = 'thinking';
      s.thinkingSince = Date.now();
    }
    return;
  }
  if (ev.type === 'item') {
    reduceItem(entry, ev);
    return;
  }
  if (LEGACY_WORKBENCH_TYPES.has(ev.type)) {
    scheduleItemsRefresh(entry);
    return;
  }
  if (ev.type === 'approval_request') {
    pushBlock(entry, { kind: 'approval', operationId: ev.operation_id,
      operationKind: ev.kind, payload: ev.payload, answered: '' });
  } else if (ev.type === 'approval_result') {
    const block = s.blocks.find((b) => b.kind === 'approval' && b.operationId === ev.operation_id);
    if (block) block.answered = ev.approved ? 'Approved' : 'Declined';
  } else if (ev.type === 'user') {
    if (ev.action || ev.run_block) {
      const action = ev.action || { verb: 'run', id: ev.run_block };
      pushBlock(entry, { kind: 'action', verb: action.verb, targetId: action.id });
    } else if (ev.text) {
      pushBlock(entry, { kind: 'user', text: ev.text });
    }
  } else if (ev.type === 'text') {
    pushBlock(entry, { kind: 'text', id: ev.id, text: ev.text, done: true });
  } else if (ev.type === 'text_delta') {
    let block = [...s.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id);
    if (!block) {
      block = { kind: 'text', id: ev.id, text: '', done: false };
      pushBlock(entry, block);
    }
    block.text += ev.text;
  } else if (ev.type === 'text_end') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'text' && b.id === ev.id && b.done === false);
    if (block) block.done = true;
  } else if (ev.type === 'report') {
    pushBlock(entry, { kind: 'report', title: ev.title, items: ev.items || [] });
  } else if (ev.type === 'session_summary') {
    s.title = ev.title || s.title;
    s.description = ev.description || '';
  } else if (ev.type === 'ask_user') {
    pushBlock(entry, { kind: 'question', callId: ev.call_id, question: ev.question,
      options: ev.options || [], responseText: '', answered: '' });
  } else if (ev.type === 'user_answer') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'question' && b.callId === ev.call_id);
    if (block) block.answered = ev.label || ev.text || 'Written response';
  } else if (ev.type === 'thinking_start') {
    thinkingBlock(entry, ev.id);
  } else if (ev.type === 'thinking_delta') {
    const block = thinkingBlock(entry, ev.id);
    block.text += ev.text;
  } else if (ev.type === 'thinking_end') {
    const block = [...s.blocks].reverse().find((b) => b.kind === 'thinking');
    if (block) block.done = true;
  } else if (ev.type === 'file_attached') {
    attachFile(entry, ev.name, ev.key);
    pushBlock(entry, { kind: 'upload', ok: true, name: ev.name, key: ev.key, frames: ev.frames || [] });
  } else if (ev.type === 'create_result') {
    if (ev.item_key || !ev.ok) {
      pushBlock(entry, {
        kind: 'created', ok: ev.ok, artifactLabel: LABELS[String(ev.item_key || '').split(':')[0]] || 'Artifact',
        action: ev.action || 'created', id: ev.id, name: ev.name, error: ev.error, itemKey: ev.item_key || ev.item || null,
      });
    }
    if (ev.ok) scheduleItemsRefresh(entry);
  } else if (ev.type === 'canvas_proposal') {
    pushBlock(entry, {
      kind: 'proposal', proposalId: ev.proposal_id, summary: ev.summary || '',
      name: ev.name || '', studioConfig: ev.studio_config || { steps: [] },
      plan: (ev.plan && ev.plan.length) ? ev.plan : [{ title: ev.summary || 'Proposed change', description: '', studio_config: ev.studio_config }],
      appliedSteps: 0, openStep: 0,
    });
  } else if (ev.type === 'workflow_chart') {
    pushBlock(entry, {
      kind: 'chart', title: ev.title || '', source: ev.source || '', vizType: ev.viz_type,
      vizConfig: ev.viz_config || {}, rows: ev.rows || [], withheld: ev.withheld_rows || 0,
      spec: ev.spec || null, savedAsSheet: false,
    });
  } else if (ev.type === 'tools') {
    s.tools = { available: ev.available || [], unavailable: ev.unavailable || [], hats: ev.hats || [] };
  } else if (ev.type === 'hat') {
    const worn = (ev.wearing && ev.wearing.length) ? ev.wearing : [{ name: ev.name, label: ev.label || ev.name, description: ev.description || '' }];
    s.hat = {
      names: worn.map((h) => h.name),
      label: worn.map((h) => h.label).join(' · '),
      description: worn.map((h) => `${h.label}: ${h.description}`).join('\n'),
    };
    s.path = ev.path || [];
    pushBlock(entry, { kind: 'meta', text: `Wearing the ${ev.label || ev.name} hat.` });
  } else if (ev.type === 'forked_from') {
    s.blocks.forEach((block) => {
      if (['question', 'approval'].includes(block.kind) && !block.answered) block.answered = '(expired)';
    });
    pushBlock(entry, { kind: 'meta', text: `Copied from ${ev.owner || 'another user'}'s session “${ev.title || ev.session_id}”. The original is unchanged.`, sensitive: true });
  } else if (ev.type === 'continued_from') {
    pushBlock(entry, { kind: 'meta', text: `Continued from “${ev.title || ev.session_id}”. Its workbench is carried over.` });
  } else if (ev.type === 'turn_done') {
    entry.pendingTurn = false;
    if (ev.cost_usd) s.totalCost += ev.cost_usd;
    if (typeof ev.prompt_tokens === 'number') s.promptTokens = ev.prompt_tokens;
    if (typeof ev.context_limit === 'number') s.contextLimit = ev.context_limit;
    scheduleItemsRefresh(entry);
  } else if (ev.type === 'stopped') {
    s.blocks.forEach((block) => {
      if (['question', 'approval'].includes(block.kind) && !block.answered) block.answered = '(expired)';
    });
    pushBlock(entry, { kind: 'stopped' });
  } else if (ev.type === 'session_end') {
    scheduleItemsRefresh(entry);
  } else if (ev.type === 'error' || ev.type === 'fatal') {
    if (ev.type === 'fatal') entry.pendingTurn = false;
    pushBlock(entry, { kind: 'error', text: ev.message });
  }
}

function messageBlocks(messages) {
  const out = [];
  for (const m of messages || []) {
    const parts = (m.content && m.content.parts) || [];
    if (parts.some((p) => p.function_response)) continue;
    const text = parts.filter((p) => p.text && !p.thought).map((p) => p.text).join('');
    if (!text) continue;
    out.push(m.role === 'user'
      ? { kind: 'user', text, at: m.created_at }
      : { kind: 'text', id: `stored-${m.sort_order}`, text, done: true, at: m.created_at });
  }
  return out;
}

function replayHistory(entry, events, messages) {
  for (const ev of events || []) {
    if (ev.seq > entry.cursor) entry.cursor = ev.seq;
    if (CONVERSATION_EVENT_TYPES.has(ev.type) || ev.type === 'item' || LEGACY_WORKBENCH_TYPES.has(ev.type)) continue;
    entry.replayAt = ev.created_at || '';
    handleEvent(entry, ev);
  }
  entry.replayAt = null;
  const blocks = entry.state.blocks.concat(messageBlocks(messages));
  blocks.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
  entry.state.blocks = blocks;
}

export {
  removeItem, deleteSavedPage, showVersion, versionsOf, currentVersion, sessionEntry, findEntryBySession, entryForSession, agentOverlay,
  hasPendingQuestion, wakePolling, stopPolling, handleEvent, replayHistory, fetchItems, pushBlock, turnUrl, applyEnvelope,
  itemKey, findItem, reduceItem, itemForRun, attachFile, applyDraftEdit, applyCellEdit, LABELS, WORKBENCH_KINDS,
};
